import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { PURPLE, MINT, hexToRgba } from '../../utils/designUtils';
import { toast } from 'react-toastify';
import { renderMarkdown, MD_STYLES } from '../../utils/markdownUtils';
import { useIsMobile } from '../../utils/responsive';

const SCOPES = [
    { value:'private', label:'개인', desc:'나만 볼 수 있어요', color:'#8b8a99', bg:'#f4f3f9', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg> },
    { value:'org', label:'조직 공유', desc:'조직 구성원에게 공유', color:PURPLE, bg:hexToRgba(PURPLE,.12), icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.5"/><path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6"/><circle cx="17" cy="7" r="2.5"/><path d="M22 18c0-2.5-2-4-4.5-4"/></svg> },
    { value:'all', label:'전체 공유', desc:'WorkiTalk 전체 사용자', color:'#5BC2B2', bg:'rgba(91,194,178,.14)', icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg> },
];


const HELP_SECTIONS = [
    {
        title: '텍스트 서식',
        items: [
            { md: '**굵게**',       desc: '굵은 텍스트' },
            { md: '*기울임*',        desc: '기울임 텍스트' },
            { md: '~~취소선~~',      desc: '취소선 텍스트' },
            { md: '`인라인 코드`',   desc: '인라인 코드' },
        ],
    },
    {
        title: '제목',
        items: [
            { md: '# 제목',    desc: 'H1 — 가장 큰 제목' },
            { md: '## 제목',   desc: 'H2 제목' },
            { md: '### 제목',  desc: 'H3 소제목' },
        ],
    },
    {
        title: '목록',
        items: [
            { md: '- 항목',       desc: '순서 없는 목록 (*, - 모두 가능)' },
            { md: '1. 항목',      desc: '순서 있는 목록' },
            { md: '- [ ] 할 일',  desc: '미완료 체크리스트' },
            { md: '- [x] 완료',   desc: '완료된 체크리스트' },
        ],
    },
    {
        title: '블록 · 링크 · 기타',
        items: [
            { md: '> 인용문',      desc: '블록 인용' },
            { md: '[텍스트](URL)', desc: '하이퍼링크 (새 탭에서 열림)' },
            { md: '---',           desc: '수평선 구분자' },
        ],
    },
];

const HELP_TIPS = [
    {
        title: '코드 블록',
        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
        code: '```python\nprint("hello")\n```',
        note: '지원 언어: python · js · ts · java · bash · json · html · css · sql · yaml · kotlin',
    },
    {
        title: '표 (Table)',
        icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>,
        code: '| 열1  | 열2  | 열3  |\n|------|:----:|-----:|\n| 값1  | 값2  | 값3  |',
        note: '정렬: |---| 기본 · |:---:| 가운데 · |---:| 오른쪽',
    },
];

export default function NoteEditorPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { user } = useTokenStore();

    const [title, setTitle] = useState('');
    const [scope, setScope] = useState('private');
    const [selectedOrgCode, setSelectedOrgCode] = useState(user?.currentOrganizationCode || '');
    const [tags, setTags] = useState([]);
    const [tagDraft, setTagDraft] = useState('');
    const [content, setContent] = useState('');
    const [showPreview, setShowPreview] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showMdHelp, setShowMdHelp] = useState(false);
    const isMobile = useIsMobile(768);

    // 모바일에서는 기본으로 미리보기 숨김
    React.useEffect(() => {
        if (isMobile) setShowPreview(false);
    }, [isMobile]);

    useEffect(() => {
        if (!isEdit) return;
        compatApi.get(`/note/${id}`)
            .then(res => {
                const d = res.data;
                setTitle(d.title || '');
                setScope(d.scope || 'private');
                setTags(d.tags || []);
                setContent(d.content || '');
                if (d.orgCode) setSelectedOrgCode(d.orgCode);
            })
            .catch(() => { toast.error('노트를 불러올 수 없습니다.'); navigate('/note'); });
    }, [id, isEdit, navigate]);

    const addTag = raw => {
        const t = String(raw||'').trim().replace(/^#/,'');
        if (!t || tags.includes(t)) { setTagDraft(''); return; }
        if (t.length > 20) { toast.warn('태그는 20자 이내로 입력해 주세요.'); return; }
        if (tags.length >= 10) { toast.warn('태그는 최대 10개까지 추가할 수 있어요.'); return; }
        setTags([...tags, t]); setTagDraft('');
    };
    const removeTag = t => setTags(tags.filter(x => x !== t));
    const onTagKey = e => {
        if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagDraft); }
        else if (e.key === 'Backspace' && !tagDraft && tags.length) removeTag(tags[tags.length-1]);
    };

    const insertSnippet = (snippet, wrap) => {
        const ta = document.getElementById('note-editor');
        if (!ta) { setContent(content + (snippet || '')); return; }
        const start = ta.selectionStart, end = ta.selectionEnd, sel = content.slice(start, end);
        const next = wrap
            ? content.slice(0,start) + wrap[0] + (sel || wrap[2] || '') + wrap[1] + content.slice(end)
            : content.slice(0,start) + snippet + content.slice(end);
        setContent(next);
        setTimeout(() => ta.focus(), 0);
    };

    const toolbarBtns = [
        { label:'H1', title:'Heading 1', run:() => insertSnippet('\n# ', null) },
        { label:'H2', title:'Heading 2', run:() => insertSnippet('\n## ', null) },
        { label:'B', title:'Bold', bold:true, run:() => insertSnippet(null,['**','**','굵게']) },
        { label:'I', title:'Italic', italic:true, run:() => insertSnippet(null,['*','*','기울임']) },
        { label:'•', title:'List', run:() => insertSnippet('\n- ', null) },
        { label:'1.', title:'Numbered', run:() => insertSnippet('\n1. ', null) },
        { label:'❝', title:'Quote', run:() => insertSnippet('\n> ', null) },
        { label:'</>', title:'Code', mono:true, run:() => insertSnippet(null,['`','`','code']) },
        { label:'▦', title:'Code block', run:() => insertSnippet('\n```\n', null) },
    ];

    const save = useCallback(async () => {
        if (!title.trim()) { toast.warn('제목을 입력해주세요.'); return; }
        if (!selectedOrgCode) { toast.warn('조직 정보를 확인할 수 없습니다. 페이지를 새로고침 해주세요.'); return; }
        setSaving(true);
        const body = { orgCode: selectedOrgCode, title: title.trim(), scope, tags, content };
        try {
            if (isEdit) {
                await compatApi.put(`/note/${id}`, body);
                toast.success('저장되었습니다.');
                navigate(`/note/${id}`);
            } else {
                const res = await compatApi.post('/note', body);
                toast.success('발행되었습니다.');
                navigate(`/note/${res.data.id}`);
            }
        } catch { toast.error('저장 중 오류가 발생했습니다.'); }
        finally { setSaving(false); }
    }, [title, scope, tags, content, selectedOrgCode, isEdit, id, navigate]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight:'calc(100vh - 61px)', background:'#f5f4fa' }}>
            <style>{MD_STYLES(PURPLE, MINT)}</style>
            <main style={{ flex: 1, width: '100%', maxWidth:1600, margin:'0 auto', padding:'32px clamp(20px,3vw,40px) 24px', boxSizing: 'border-box' }}>
                <div style={{ display:'flex', alignItems:'center', marginBottom:18, gap:12 }}>
                    <button onClick={() => navigate('/note')} style={{ background:'white', border:'1px solid #ececf2', height:36, width:36, borderRadius:10, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', color:'#6a6878' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                    <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:'#1f1d2e' }}>{isEdit ? '노트 수정' : '새 노트 작성'}</h1>
                </div>

                {/* Title + scope + tags */}
                <div style={{ background:'white', borderRadius:16, border:'1px solid #f0eff5', boxShadow:'0 1px 2px rgba(60,50,120,.04)', padding:'20px 22px', marginBottom:16 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                        <span style={{ fontSize:12.5, fontWeight:600, color:'#1f1d2e' }}>제목 <span style={{ color:'#ef4444' }}>*</span></span>
                    </div>
                    <input value={title} onChange={e => setTitle(e.target.value)} placeholder="제목을 입력하세요"
                         style={{ width:'100%', boxSizing:'border-box', padding:'10px 12px', borderRadius:10, background:'#fafaff', border:'1px solid #ececf2', outline:'none', fontSize:18, fontWeight:700, color:'#1f1d2e', fontFamily:'inherit', marginBottom:16 }}/>
                    <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                        {SCOPES.map(s => {
                            const active = scope === s.value;
                            return (
                                <button key={s.value} onClick={() => setScope(s.value)} style={{ display:'inline-flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:10, background: active ? s.bg : '#fafaff', border:`1px solid ${active ? s.color : '#ececf2'}`, cursor:'pointer', fontFamily:'inherit', textAlign:'left', transition:'all 120ms' }}>
                                    <span style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        background: active ? 'white' : s.bg,
                                        color: s.color,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }}>{s.icon}</span>
                                    <span style={{ display:'flex', flexDirection:'column', lineHeight:1.25 }}>
                                        <span style={{ fontSize:13, fontWeight:600, color: active ? s.color : '#1f1d2e' }}>{s.label}</span>
                                        <span style={{ fontSize:11, color:'#8b8a99', marginTop:2 }}>{s.desc}</span>
                                    </span>
                                    {active && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5L9 17.5L20 6.5"/></svg>}
                                </button>
                            );
                        })}
                    </div>
                    {/* 조직 선택 (조직 공유 선택 시에만 표시) */}
                    {scope === 'org' && (
                        <div style={{ marginTop:14, padding:'14px 16px', borderRadius:10, background:hexToRgba(PURPLE,.05), border:`1px solid ${hexToRgba(PURPLE,.18)}` }}>
                            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                                <div style={{ width:22, height:22, borderRadius:6, background:hexToRgba(PURPLE,.12), color:PURPLE, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                                </div>
                                <span style={{ fontSize:12.5, fontWeight:600, color:'#1f1d2e' }}>공유할 조직</span>
                                <span style={{ fontSize:11, color:'#aaa9b7' }}>· 선택한 조직 구성원에게 공개됩니다</span>
                            </div>
                            <select
                                value={selectedOrgCode}
                                onChange={e => setSelectedOrgCode(e.target.value)}
                                style={{ width:'100%', height:38, borderRadius:8, border:`1.5px solid ${hexToRgba(PURPLE,.3)}`, padding:'0 12px', fontSize:13.5, color:'#1f1d2e', background:'white', fontFamily:'inherit', outline:'none', cursor:'pointer' }}
                                onFocus={e => { e.currentTarget.style.borderColor = PURPLE; }}
                                onBlur={e => { e.currentTarget.style.borderColor = hexToRgba(PURPLE,.3); }}
                            >
                                {(user?.orgUserList || []).filter(org => org.isApproved).map(org => (
                                    <option key={org.organizationCode} value={org.organizationCode}>
                                        {org.organizationName} ({org.organizationCode})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Tags */}
                    <div style={{ marginTop:18, paddingTop:16, borderTop:'1px solid #f4f3f9' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                            <div style={{ width:22, height:22, borderRadius:6, background:hexToRgba(PURPLE,.12), color:PURPLE, display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12l-8 8a2 2 0 0 1-2.8 0L3 13.8V4h9.8L20 11.2a2 2 0 0 1 0 .8z"/><circle cx="7.5" cy="7.5" r="1.2" fill="currentColor"/></svg>
                            </div>
                            <span style={{ fontSize:12.5, fontWeight:600, color:'#1f1d2e' }}>태그</span>
                            <span style={{ fontSize:11, color:'#aaa9b7' }}>· Enter 또는 쉼표로 추가, 최대 10개</span>
                        </div>
                        <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:6, padding:'8px 10px', borderRadius:10, background:'#fafaff', border:'1px solid #ececf2', minHeight:44 }}>
                            {tags.map(t => (
                                <span key={t} style={{ display:'inline-flex', alignItems:'center', gap:6, background:hexToRgba(PURPLE,.1), color:PURPLE, border:`1px solid ${hexToRgba(PURPLE,.2)}`, padding:'4px 4px 4px 10px', borderRadius:999, fontSize:12, fontWeight:500 }}>
                                    #{t}
                                    <button onClick={() => removeTag(t)} style={{ background:'rgba(255,255,255,.7)', border:'none', borderRadius:'50%', width:16, height:16, padding:0, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', color:PURPLE }}>
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
                                    </button>
                                </span>
                            ))}
                            <input value={tagDraft} onChange={e => setTagDraft(e.target.value)} onKeyDown={onTagKey} onBlur={() => addTag(tagDraft)}
                                placeholder={tags.length ? '' : '예) 로드맵, Q3, 회의록'}
                                style={{ flex:1, minWidth:120, background:'transparent', border:'none', outline:'none', fontSize:13, color:'#1f1d2e', fontFamily:'inherit', padding:'6px 4px' }}/>
                        </div>
                    </div>
                </div>

                {/* Editor + Preview */}
                <div style={{ display:'grid', gridTemplateColumns: showPreview ? '1fr 1fr' : '1fr', gap:16 }}>
                    <div style={{ background:'white', borderRadius:16, border:'1px solid #f0eff5', boxShadow:'0 1px 2px rgba(60,50,120,.04)', overflow:'hidden', display:'flex', flexDirection:'column', minHeight:520 }}>
                        <div style={{ display:'flex', gap:2, padding:'8px 10px', borderBottom:'1px solid #f0eff5', background:'#fafaff', flexWrap:'wrap' }}>
                            {toolbarBtns.map((b,i) => (
                                <button key={i} title={b.title} onClick={b.run} style={{ width:b.label.length>1?'auto':30, height:30, padding:'0 8px', background:'transparent', border:'none', borderRadius:6, color:'#3a3849', fontSize:13, fontWeight:b.bold?700:500, fontStyle:b.italic?'italic':'normal', fontFamily:b.mono?'ui-monospace,monospace':'inherit', cursor:'pointer' }}
                                    onMouseEnter={e => { e.currentTarget.style.background=hexToRgba(PURPLE,.1); e.currentTarget.style.color=PURPLE; }}
                                    onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#3a3849'; }}>
                                    {b.label}
                                </button>
                            ))}
                            <div style={{ flex:1 }}/>
                            <span style={{ fontSize:11, color:'#8b8a99', alignSelf:'center' }}>{content.length.toLocaleString()}자</span>
                            <div style={{ width:1, height:16, background:'#ececf2', alignSelf:'center', margin:'0 6px' }}/>
                            <button onClick={() => setShowMdHelp(true)} title="마크다운 도움말"
                                style={{ height:28, padding:'0 10px', background:'transparent', border:'none', borderRadius:6, color:'#8b8a99', fontSize:12, fontWeight:500, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5, fontFamily:'inherit' }}
                                onMouseEnter={e => { e.currentTarget.style.background=hexToRgba(PURPLE,.1); e.currentTarget.style.color=PURPLE; }}
                                onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='#8b8a99'; }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                마크다운 도움말
                            </button>
                        </div>
                        <textarea id="note-editor" value={content} onChange={e => setContent(e.target.value)}
                            placeholder={`# 제목\n\n마크다운으로 작성하세요...\n\n- 목록\n- **굵게**, *기울임*, \`코드\`\n\n> 인용문`}
                            spellCheck={false}
                            style={{ flex:1, width:'100%', padding:'18px 22px', background:'transparent', border:'none', outline:'none', resize:'none', fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:13.5, lineHeight:1.7, color:'#2a2a3a', minHeight:480, boxSizing:'border-box' }}/>
                    </div>
                    {showPreview && (
                        <div style={{ background:'white', borderRadius:16, border:'1px solid #f0eff5', boxShadow:'0 1px 2px rgba(60,50,120,.04)', overflow:'hidden', display:'flex', flexDirection:'column', minHeight:520 }}>
                            <div style={{ padding:'11px 18px', borderBottom:'1px solid #f0eff5', background:'#fafaff', display:'flex', alignItems:'center', gap:8 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8b8a99" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
                                <span style={{ fontSize:12, fontWeight:500, color:'#6a6878' }}>미리보기</span>
                            </div>
                            <div className="md-body" style={{ padding:'20px 24px', overflow:'auto', flex:1 }}
                                dangerouslySetInnerHTML={{ __html: renderMarkdown(content || '_내용을 입력하면 여기에 표시됩니다._') }}/>
                        </div>
                    )}
                </div>
            </main>

            {/* Markdown help modal */}
            {showMdHelp && (
                <div onClick={() => setShowMdHelp(false)}
                    style={{ position:'fixed', inset:0, background:'rgba(30,25,60,.52)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000, backdropFilter:'blur(3px)', padding:'20px' }}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ background:'white', borderRadius:20, width:'100%', maxWidth:580, maxHeight:'86vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 24px 60px -12px rgba(60,50,120,.28)' }}>

                        {/* 헤더 */}
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px', borderBottom:'1px solid #f0eff5', flexShrink:0 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                                <div style={{ width:34, height:34, borderRadius:10, background:hexToRgba(PURPLE,.1), color:PURPLE, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                </div>
                                <div>
                                    <div style={{ fontSize:15, fontWeight:700, color:'#1f1d2e' }}>마크다운 도움말</div>
                                    <div style={{ fontSize:11.5, color:'#8b8a99', marginTop:1 }}>지원되는 마크다운 문법 전체 목록</div>
                                </div>
                            </div>
                            <button onClick={() => setShowMdHelp(false)}
                                style={{ width:32, height:32, borderRadius:8, background:'#f5f4fa', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#6a6878', flexShrink:0 }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                            </button>
                        </div>

                        {/* 스크롤 영역 */}
                        <div style={{ overflow:'auto', padding:'20px 22px', display:'flex', flexDirection:'column', gap:18 }}>

                            {/* 인라인 문법 섹션 */}
                            {HELP_SECTIONS.map(section => (
                                <div key={section.title}>
                                    <div style={{ fontSize:10.5, fontWeight:700, color:PURPLE, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8, paddingLeft:2 }}>{section.title}</div>
                                    <div style={{ borderRadius:12, border:'1px solid #f0eff5', overflow:'hidden' }}>
                                        {section.items.map((item, idx) => (
                                            <div key={idx} style={{ display:'flex', alignItems:'center', padding:'9px 14px', borderBottom: idx < section.items.length - 1 ? '1px solid #f5f4fb' : 'none', background: idx % 2 === 0 ? 'white' : '#fafaff', gap:14 }}>
                                                <code style={{ flexShrink:0, fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12.5, color:'#3a3849', background:hexToRgba(PURPLE,.07), padding:'3px 10px', borderRadius:6, whiteSpace:'pre' }}>{item.md}</code>
                                                <span style={{ fontSize:13, color:'#5d5b6e' }}>{item.desc}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {/* 코드 블록 · 표 팁 카드 */}
                            {HELP_TIPS.map(tip => (
                                <div key={tip.title}>
                                    <div style={{ fontSize:10.5, fontWeight:700, color:PURPLE, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8, paddingLeft:2 }}>{tip.title}</div>
                                    <div style={{ borderRadius:12, border:'1px solid #f0eff5', overflow:'hidden' }}>
                                        {/* 코드 예시 */}
                                        <div style={{ background:'#1f1d2e', padding:'12px 16px', display:'flex', alignItems:'center', gap:10 }}>
                                            <span style={{ color:hexToRgba(MINT,.8), flexShrink:0 }}>{tip.icon}</span>
                                            <code style={{ fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace', fontSize:12.5, color:'#e6e5f0', whiteSpace:'pre', lineHeight:1.7 }}>{tip.code}</code>
                                        </div>
                                        {/* 설명 */}
                                        <div style={{ padding:'9px 14px', background:'#fafaff', display:'flex', alignItems:'flex-start', gap:8 }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={PURPLE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, marginTop:1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                            <span style={{ fontSize:12.5, color:'#5d5b6e', lineHeight:1.55 }}>{tip.note}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <p style={{ fontSize:11.5, color:'#c0bfcc', textAlign:'center', margin:'2px 0 0', lineHeight:1.6 }}>
                                배경 클릭 또는 ✕ 버튼으로 닫기
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Sticky bottom bar */}
            <div style={{ position:'sticky', bottom:0, left:0, right:0, background:'rgba(255,255,255,.92)', backdropFilter:'blur(10px)', borderTop:'1px solid #ececf2', padding:'12px clamp(20px,3vw,40px)', display:'flex', justifyContent:'flex-end', alignItems:'center', gap:8, zIndex:20, flexWrap:'wrap' }}>
                <button onClick={() => setShowPreview(!showPreview)} style={{ height:40, padding:'0 16px', borderRadius:10, background: showPreview ? hexToRgba(PURPLE,.1) : 'white', border:`1.5px solid ${showPreview ? hexToRgba(PURPLE,.3) : '#ececf2'}`, color: showPreview ? PURPLE : '#5d5b6e', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:6, marginRight:'auto' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>
                    미리보기
                </button>
                <button onClick={() => navigate('/note')} style={{ height:40, padding:'0 18px', borderRadius:10, background:'white', border:'1.5px solid #ececf2', color:'#5d5b6e', fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:'inherit' }}>취소</button>
                <button onClick={save} disabled={saving} style={{ height:40, padding:'0 22px', borderRadius:10, border:'none', background:`linear-gradient(135deg,${PURPLE},${MINT})`, color:'white', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:'inherit', boxShadow:`0 6px 14px -6px ${hexToRgba(PURPLE,.55)}`, display:'inline-flex', alignItems:'center', gap:6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {isEdit ? <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2zM17 21v-8H7v8M7 3v5h8"/> : <><path d="m22 2-7 20-4-9-9-4z"/><path d="M22 2 11 13"/></>}
                    </svg>
                    {isEdit ? '저장' : '발행'}
                </button>
            </div>
        </div>
    );
}
